import React from 'react';
import {
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Box,
    Typography,
    Button,
    Grid,
    Autocomplete,
    CircularProgress,
    SxProps,
    Theme
} from '@mui/material';
import useValidation, { ValidationRule } from './hooks/useValidation';
import { useTranslation } from 'react-i18next';
import { mimeTypes } from '../../utils/common';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

const exceptNumericSymbols = ['e', 'E', '+', '-', '.']; //estos símbolos son numéricos "válidos" pero no deben existir en el campo cuit

//Permite deshabilitar la interacción de los inputs.
export const getOnlyVisualStyles = (onlyVisual: boolean): SxProps<Theme> =>
    onlyVisual
      ? {
          pointerEvents: 'none',
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#c4c4c4',
            borderWidth: '1px'
          },
          '& .Mui-focused': {
            color: '#476773',
            backgroundColor: 'inherit'
          }
        }
      : {};
      
export interface FieldConfig {
    type:
        | 'text'
        | 'textarea'
        | 'select'
        | 'checkbox'
        | 'file'
        | 'autocomplete'
        | 'autocomplete-default'
        | 'autocomplete-multiple'
        | 'date'
        | 'date-mui';
    label: string;
    name: string;
    value: any;
    options?: any;
    noOptionsText?: string;
    required?: boolean;
    helperText?: string;
    maxLength?: number;
    minLength?: number;
    inputType?: 'text' | 'number';
    testID?: string;
    gridSpace?: { xs?: number; sm?: number; md?: number; lg?: number };
    readonly?: boolean;
    disabled?: boolean;
    bottomText?: string;
    maxFileNumbers?: number;
    allowedFileTypes?: string[];
    loading?: boolean;
    optionAdapter?: (option: any) => any;
    onBlurValidation?: any;
    getOptionLabel?: (option: any) => string;
    multiple?: boolean;
    validations?: ValidationRule[];
    sx?: SxProps<Theme>;
    minDate?: string;
    maxDate?: string;
}

interface CustomFieldProps {
    config: FieldConfig;
    value: any;
    onChange: (name: string, value: any) => void;
    handleInputChange?: any;
    inputValue?: string;
    error?: boolean;
    helperText?: string;
    onBlurValidation?: (name: string, hasError: boolean, errorText: string) => void;
    margin?: 'normal' | 'none' | 'dense';
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    onlyVisual?: boolean;
}

export const commonInputStyle = {
    // '& .MuiOutlinedInput-root': {
    //     backgroundColor: '#E0EBED'
    // },
    '& .MuiInputLabel-root': {
        display: 'flex',
        alignItems: 'center',
        top: '50%',
        transform: 'translateY(-50%)',
        paddingLeft: 2,
        color: '#476773',
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1
    },
    '& .MuiAutocomplete-inputRoot': {
        fontSize: '1rem'
    },
    '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled':
        {
            transform: 'translate(.5rem, -2.3rem) scale(0.75)'
        },
    '& .MuiInputLabel-asterisk': {
        color: '#F44336',
        fontSize: '1rem'
    }
};

const CustomField: React.FC<CustomFieldProps> = ({
    config,
    value,
    onChange,
    onlyVisual = false,
    handleInputChange,
    inputValue,
    error,
    helperText,
    margin,
    onKeyDown
}) => {
    const {
        type,
        label,
        name,
        options,
        required,
        inputType,
        maxLength,
        minLength,
        testID,
        maxFileNumbers = 1,
        readonly,
        bottomText,
        allowedFileTypes = [],
        disabled,
        onBlurValidation,
        getOptionLabel = (option: any) => option.nombre || '',
        multiple,
        validations,
        noOptionsText,
        sx
    } = config;

    const { t } = useTranslation('validations');
    const { t: tCommon } = useTranslation('common');

    const { validate } = useValidation(t);

    const handleBlur = () => {
        const { hasError, errorText } = validate(value, validations || []);

        if (onBlurValidation) {
            onBlurValidation(config.name, hasError, errorText);
        }
    };

    const handleChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        if (onlyVisual) return;
        onChange(e.target.name || '', e.target.value);
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (inputType === 'number' && maxLength && e.target.value.length > maxLength) {
            e.target.value = e.target.value.slice(0, maxLength);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;

        if (files && files.length > 0) {
            if (maxFileNumbers && files.length > maxFileNumbers) {
                return;
            }

            const validFiles = Array.from(files).filter((file) => {
                const extension = file.name.split('.').pop()?.toLowerCase();
                if (extension && allowedFileTypes.includes(extension)) {
                    const mimeType = mimeTypes[extension];
                    return mimeType ? file.type === mimeType : false;
                }
                return false;
            });

            if (validFiles.length !== files.length) {
                return;
            }

            onChange(name, validFiles.length === 1 ? validFiles[0] : validFiles);
        }
    };

    const handleFocus = () => {
        if (error) {
            onChange(config.name, value);
        }
    };

    let inputField;

    switch (type) {
        case 'text':
            inputField = (
                <>
                    <TextField
                        fullWidth
                        size="small"
                        label={label}
                        name={name}
                        value={value}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        margin={margin ? margin : 'normal'}
                        required={required}
                        error={error}
                        disabled={disabled}
                        inputProps={{
                            maxLength: inputType === 'text' ? maxLength : undefined,
                            pattern: inputType === 'number' ? '\\d*' : undefined,
                            onInput: handleInput,
                            'data-testid': testID
                        }}
                        onKeyDown={
                            onlyVisual
                                ? undefined
                                : (e) => {
                                      if (inputType === 'number' && exceptNumericSymbols.includes(e.key)) {
                                          e.preventDefault();
                                      }
                                      if (onKeyDown) onKeyDown(e);
                                  }
                        }
                        InputProps={{
                            sx: { marginLeft: 0, marginRight: 0, marginBottom: error ? 0 : 0.5 },
                            readOnly: readonly,
                        }}
                        type={inputType}
                        sx={{
                            ...commonInputStyle,
                            ...getOnlyVisualStyles(onlyVisual)
                          }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#476773',
                            fontSize: '12px',
                            marginTop: '-5px',
                            paddingLeft: '2px'
                        }}
                    >
                        {bottomText}
                    </Typography>
                </>
            );
            break;

        case 'textarea':
            inputField = (
                <>
                    <TextField
                        fullWidth
                        label={label}
                        name={name}
                        value={value}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        margin={margin ? margin : 'normal'}
                        required={required}
                        error={error}
                        disabled={disabled}
                        multiline
                        rows={6}
                        onKeyDown={
                            onlyVisual
                                ? undefined
                                : (e) => {
                                      if (inputType === 'number' && exceptNumericSymbols.includes(e.key)) {
                                          e.preventDefault();
                                      }
                                      if (onKeyDown) onKeyDown(e);
                                  }
                        }
                        inputProps={{
                            maxLength: maxLength,
                            onInput: handleInput,
                            'data-testid': testID
                        }}
                        InputProps={{
                            sx: { marginLeft: 0, marginRight: 0, marginBottom: error ? 0 : 0.5 },
                            readOnly: readonly
                        }}
                        sx={{
                            '& .MuiInputLabel-asterisk': {
                                color: '#F44336',
                                fontSize: 18
                            },
                            ...getOnlyVisualStyles(onlyVisual)
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#476773',
                            fontSize: '12px',
                            marginTop: '-5px',
                            paddingLeft: '2px'
                        }}
                    >
                        {bottomText}
                    </Typography>
                </>
            );
            break;
        case 'select':
            inputField = (
                <TextField
                    fullWidth
                    size="small"
                    select
                    disabled={disabled}
                    label={label}
                    name={name}
                    value={value}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    margin="normal"
                    InputProps={{
                        sx: { marginLeft: 0, marginRight: 0, marginBottom: error ? 0 : 0.5 }
                    }}
                    inputProps={{
                        'data-testid': testID
                    }}
                    required={required}
                    error={error}
                    onKeyDown={
                        onlyVisual
                            ? undefined
                            : (e) => {
                                  e.preventDefault();
                                  if (onKeyDown) onKeyDown(e);
                              }
                    }
                    sx={{
                        ...commonInputStyle,
                        ...getOnlyVisualStyles(onlyVisual)
                    }}
                >
                    {options?.map((option: any) => {
                        const adaptedOption = config.optionAdapter
                            ? config.optionAdapter(option)
                            : option;
                        return (
                            <MenuItem key={adaptedOption.id} value={adaptedOption.id}>
                                {adaptedOption.nombre}
                            </MenuItem>
                        );
                    })}
                </TextField>
            );
            break;
        case 'checkbox':
            inputField = (
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                inputProps={
                                    {
                                        'data-testid': testID
                                    } as React.InputHTMLAttributes<HTMLInputElement>
                                }
                                checked={value}
                                onChange={handleChange}
                                name={name}
                                sx={{
                                    pl: 0 // Removes left padding only
                                }}
                            />
                        }
                        label={<Typography variant="body1">{label}</Typography>}
                    />
                </Box>
            );
            break;
        case 'file':
            inputField = (
                <>
                    <TextField
                        size="small"
                        fullWidth
                        label={label}
                        name={name}
                        type="file"
                        onChange={handleFileChange}
                        margin="normal"
                        required={required}
                        error={error}
                        onBlur={handleBlur}
                        disabled={disabled}
                        InputLabelProps={{
                            shrink: true // Keeps the label visible when no file is selected
                        }}
                        inputProps={{
                            accept:
                                allowedFileTypes.length > 0
                                    ? allowedFileTypes.map((type) => `.${type}`).join(',')
                                    : undefined,
                            multiple: maxFileNumbers > 1,
                            sx: { marginLeft: 0, marginRight: 0, marginBottom: error ? 0 : 0.5 },
                            'data-testid': testID
                        }}
                        sx={{
                            '& .MuiInputLabel-asterisk': {
                                color: '#F44336',
                                fontSize: 18,
                                padding: 0
                            },
                            height: 40,
                            marginLeft: 0,
                            ...getOnlyVisualStyles(onlyVisual)
                        }}
                    />
                    <Grid item sx={{ marginTop: 2 }}>
                        {bottomText && (
                            <Typography
                                variant="caption"
                                sx={{ color: '#476773', fontSize: '12px', marginLeft: '4px' }}
                            >
                                {bottomText}
                            </Typography>
                        )}
                    </Grid>
                </>
            );
            break;
        //only saves id
        case 'autocomplete':
            inputField = (
                <Autocomplete
                    size="small"
                    disabled={disabled}
                    noOptionsText={noOptionsText || tCommon('inputs.autocomplete.noOptions')}
                    value={options.find((option: any) => option.id === value) || null}
                    options={options || []}
                    fullWidth
                    getOptionLabel={getOptionLabel}
                    onChange={
                        onlyVisual
                            ? undefined
                            : (_e, newValue) => onChange(name, newValue ? newValue.id : null)
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={label}
                            required={required}
                            error={error}
                            onFocus={onlyVisual ? undefined : handleFocus}
                            onBlur={onlyVisual ? undefined : handleBlur}
                            fullWidth
                            onKeyDown={onlyVisual ? (e) => e.preventDefault() : undefined}
                            InputProps={{
                                ...params.InputProps,
                                sx: {
                                    height: 40,
                                    marginLeft: 0,
                                    marginRight: 0,
                                    marginBottom: error ? 0 : 0.5,
                                    ...getOnlyVisualStyles(onlyVisual)
                                },
                                endAdornment: (
                                    <>
                                        {config.loading ? (
                                            <CircularProgress color="inherit" size={20} />
                                        ) : (
                                            params.InputProps.endAdornment
                                        )}
                                    </>
                                )
                            }}
                            inputProps={{
                                ...params.inputProps,
                                readOnly: onlyVisual,
                                'data-testid': testID
                            }}
                            sx={{
                                ...commonInputStyle,
                                ...getOnlyVisualStyles(onlyVisual)
                            }}
                        />
                    )}
                />
            );
            break;
        //saves the full object
        case 'autocomplete-default':
            inputField = (
                <>
                    <Autocomplete
                        size="small"
                        disabled={disabled}
                        value={config?.multiple ? value || [] : value}
                        multiple={multiple}
                        noOptionsText={noOptionsText || tCommon('inputs.autocomplete.noOptions')}
                        inputValue={inputValue}
                        options={options || []}
                        fullWidth
                        onInputChange={onlyVisual ? undefined : handleInputChange}
                        getOptionLabel={getOptionLabel}
                        onChange={
                            onlyVisual
                                ? undefined
                                : (_e, newValue) => onChange(name, newValue ?? null)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                required={required}
                                error={error}
                                onFocus={onlyVisual ? undefined : handleFocus}
                                onBlur={onlyVisual ? undefined : handleBlur}
                                fullWidth
                                InputProps={{
                                    ...params.InputProps,
                                    sx: {
                                        height: multiple ? 'auto' : 40,
                                        marginLeft: 0,
                                        marginRight: 0,
                                        marginBottom: error ? 0 : 0.5,
                                        ...getOnlyVisualStyles(onlyVisual)
                                    },
                                    endAdornment: (
                                        <>
                                            {config.loading ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : (
                                                params.InputProps.endAdornment
                                            )}
                                        </>
                                    )
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: onlyVisual,
                                    'data-testid': testID
                                }}
                                className={multiple && value?.length > 0 ? 'label-shrink' : ''}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        color: '#476773',
                                        fontSize: '1rem',
                                        top: multiple && value?.length > 0 ? 5 : 10,
                                        left: multiple && value?.length > 0 ? 1 : 2,
                                        fontWeight: 400
                                    },
                                    '& .MuiAutocomplete-inputRoot': {
                                        fontSize: '1rem'
                                    },
                                    '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled, & .MuiInputLabel-root.label-shrink':
                                        {
                                            transform: 'translate(1.3rem, -1rem) scale(0.75)'
                                        },
                                    '& .MuiInputLabel-asterisk': {
                                        color: '#F44336',
                                        fontSize: '1rem'
                                    },
                                    ...getOnlyVisualStyles(onlyVisual)
                                }}
                            />
                        )}
                    />
                    {bottomText && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#476773',
                                fontSize: '12px',
                                marginTop: '-5px',
                                paddingLeft: '2px'
                            }}
                        >
                            {bottomText}
                        </Typography>
                    )}
                </>
            );
            break;
        case 'date':
            inputField = (
                <>
                    <TextField
                        fullWidth
                        label={label}
                        name={name}
                        type="date"
                        value={value}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        margin={margin ? margin : 'normal'}
                        required={required}
                        error={error}
                        disabled={disabled}
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            'data-testid': testID,
                            min: config?.minDate,
                            max: config?.maxDate,
                            readOnly: onlyVisual || readonly
                        }}
                        InputProps={{
                            sx: { marginLeft: 0, marginRight: 0, marginBottom: error ? 0 : 0.5 }
                        }}
                        sx={{
                            ...commonInputStyle,
                            ...getOnlyVisualStyles(onlyVisual)
                        }}
                    />
                    {bottomText && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#476773',
                                fontSize: '12px',
                                marginTop: '-5px',
                                paddingLeft: '2px'
                            }}
                        >
                            {bottomText}
                        </Typography>
                    )}
                </>
            );
            break;
            case 'date-mui':
                inputField = (
                    <>
                        <DatePicker
                            label={label}
                            format="DD/MM/YYYY"
                            value={value ? dayjs(value) : null}
                            onChange={(newValue: Dayjs | null) =>
                                handleChange({
                                    target: { name, value: newValue }
                                } as unknown as React.ChangeEvent<{ name?: string; value: unknown }>)
                            }
                            onOpen={handleFocus}
                            onClose={handleBlur}
                            disabled={disabled}
                            readOnly={readonly}
                            minDate={config?.minDate ? dayjs(config.minDate) : undefined}
                            maxDate={config?.maxDate ? dayjs(config.maxDate) : undefined}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    margin: margin ? margin : 'normal',
                                    required: required,
                                    error: error,
                                    InputLabelProps: { shrink: true },
                                    inputProps: { 'data-testid': testID },
                                    sx: {
                                        ...commonInputStyle,
                                        ...getOnlyVisualStyles(onlyVisual)
                                    }
                                }
                            }}
                        />
                        {bottomText && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#476773',
                                    fontSize: '12px',
                                    marginTop: '-5px',
                                    paddingLeft: '2px'
                                }}
                            >
                                {bottomText}
                            </Typography>
                        )}
                    </>
                );
                break;
        default:
            inputField = null;
    }

    return (
        <>
            {inputField}
            {error && (
                <Typography color="error" variant="caption">
                    {helperText}
                </Typography>
            )}
        </>
    );
};

export default CustomField;
